/**
 * Token-Kit Developer Portal — Developer Service
 *
 * Real API calls to the Token-Kit backend for dashboard metrics,
 * API key management, analytics, and usage logs.
 *
 * Webhooks, billing, and settings are still placeholder/coming soon
 * as those backend routes are built in later phases.
 */

import { api } from './api.client';
import {
  DashboardMetrics,
  ApiKey,
  CreateApiKeyResult,
  AnalyticsData,
  Webhook,
  BillingInfo,
  Invoice,
  TierInfo,
  DeveloperSettings,
  DeveloperApp,
} from '../types';
import { getDeveloperTokenTransactions } from './developer-token.service';

// -- Dashboard --------------------------------------------------------

export async function getDashboardMetrics(): Promise<DashboardMetrics> {
  return api.get<DashboardMetrics>('/portal/me/metrics');
}

// -- API Keys ---------------------------------------------------------

export async function getApiKeys(): Promise<ApiKey[]> {
  // Backend returns `status: 'active' | 'revoked'`; map to `isActive: boolean` for the UI.
  const raw = await api.get<(Omit<ApiKey, 'isActive'> & { status: string })[]>('/portal/me/api-keys');
  return raw.map((k) => ({ ...k, isActive: k.status === 'active' }));
}

export async function createApiKey(
  name: string,
  environment: string,
  _allowedDomains?: string[]
): Promise<CreateApiKeyResult> {
  return api.post<CreateApiKeyResult>('/portal/me/api-keys', { name, environment });
}

export async function deleteApiKey(id: string): Promise<void> {
  await api.delete(`/portal/me/api-keys/${id}`);
}

export async function toggleApiKey(id: string): Promise<void> {
  await api.patch(`/portal/me/api-keys/${id}`);
}

// -- Analytics --------------------------------------------------------

export async function getAnalytics(period: string = '7d'): Promise<AnalyticsData> {
  return api.get<AnalyticsData>('/portal/me/analytics', { period });
}

// -- Webhooks --------------------------------------------------------

export async function getWebhooks(): Promise<Webhook[]> {
  return api.get<Webhook[]>('/portal/me/webhooks');
}

export async function createWebhook(_url: string, _events: string[]): Promise<Webhook> {
  return api.post<Webhook>('/portal/me/webhooks', {
    url: _url,
    events: _events,
  });
}

export async function deleteWebhook(_id: string): Promise<void> {
  await api.delete(`/portal/me/webhooks/${_id}`);
}

export async function testWebhook(_id: string): Promise<boolean> {
  const result = await api.post<{ ok: boolean }>(`/portal/me/webhooks/${_id}/test`, {});
  return result.ok;
}

// -- Billing (placeholder - payment integration coming in Phase 3) ----

export async function getBillingInfo(): Promise<BillingInfo> {
  return {
    currentTier: 'free',
    monthlySpend: 0,
    nextBillingDate: '',
    paymentMethod: null,
  };
}

export async function getInvoices(): Promise<Invoice[]> {
  const transactions = await getDeveloperTokenTransactions();

  // Map purchase and refund developer token transactions into the Invoice shape
  const devTokenInvoices: Invoice[] = transactions
    .filter((t) => t.type === 'purchase' || t.type === 'refund')
    .map((t) => ({
      id: t.id,
      date: t.createdAt,
      description: t.description,
      amount: t.amountUsd ?? 0,
      status: (t.status === 'completed' ? 'paid' : t.status) as Invoice['status'],
      downloadUrl: '',
    }));

  // Sort newest first
  return devTokenInvoices.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
}

export function getTierOptions(): TierInfo[] {
  return [
    {
      tier: 'free',
      name: 'Free',
      monthlyPrice: 0,
      rateLimit: 500,
      maxApiKeys: 3,
      features: ['Full SDK access', 'Community support', 'Developer dashboard', '3 API keys'],
    },
    {
      tier: 'pro',
      name: 'Pro',
      monthlyPrice: 25,
      rateLimit: 2000,
      maxApiKeys: 10,
      features: ['Everything in Free', 'Priority support (24h SLA)', 'Advanced analytics', 'Webhooks', '10 API keys'],
    },
    {
      tier: 'enterprise',
      name: 'Enterprise',
      monthlyPrice: -1,
      rateLimit: -1,
      maxApiKeys: -1,
      features: ['Everything in Pro', 'SLA guarantee', 'Custom model routing', 'Dedicated support', 'White-label options', 'Unlimited API keys'],
    },
  ];
}

// -- Apps (Developer App CRUD) ----------------------------------------

export async function getApps(): Promise<DeveloperApp[]> {
  return api.get<DeveloperApp[]>('/portal/me/apps');
}

export async function createApp(
  appName: string,
  allowedOrigins: string[]
): Promise<DeveloperApp> {
  return api.post<DeveloperApp>('/portal/me/apps', { appName, allowedOrigins });
}

export async function updateApp(
  clientId: string,
  updates: { appName?: string; allowedOrigins?: string[] }
): Promise<DeveloperApp> {
  return api.patch<DeveloperApp>(`/portal/me/apps/${clientId}`, updates);
}

export async function deleteApp(clientId: string): Promise<void> {
  await api.delete(`/portal/me/apps/${clientId}`);
}

// -- Settings ---------------------------------------------------------

export async function getDeveloperSettings(): Promise<DeveloperSettings> {
  const profile = await api.get<{
    email: string;
    namespace: string;
    websiteUrl?: string;
  }>('/portal/me');
  return {
    namespace: profile.namespace || '',
    contactEmail: profile.email || '',
    websiteUrl: profile.websiteUrl || '',
    redirectUrl: '',
    twoFactorEnabled: false,
    ipAllowListEnabled: false,
    notifications: {
      usageAlerts: true,
      billingAlerts: true,
      securityAlerts: true,
      weeklyDigest: false,
    },
  };
}

export async function updateDeveloperSettings(
  updates: Pick<DeveloperSettings, 'namespace' | 'websiteUrl'>
): Promise<DeveloperSettings> {
  await api.patch('/portal/me', {
    namespace: updates.namespace || undefined,
    websiteUrl: updates.websiteUrl || undefined,
  });
  return getDeveloperSettings();
}